import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import type { ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { tokens } from '../../../design';

/**
 * The mockup's `.pcard` (AUT-403): a titled card with an optional «· sub»
 * and a right-aligned action, plus `InfoRow` for its label / value lines.
 */
export const SectionCard = ({
  title,
  sub,
  action,
  children,
  tone,
}: {
  title: string;
  sub?: string;
  action?: ReactNode;
  children: ReactNode;
  tone?: 'danger' | 'warning';
}): ReactElement => (
  <Paper
    variant="outlined"
    sx={{
      overflow: 'hidden',
      ...(tone === 'danger' ? { borderColor: tokens.state.danger } : {}),
      ...(tone === 'warning' ? { borderColor: tokens.state.warning } : {}),
    }}
  >
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        px: 2.25,
        py: 1.5,
        borderBottom: `1px solid ${tokens.border.default}`,
      }}
    >
      <Typography variant="subtitle2" fontWeight={700} sx={{ flex: 1 }}>
        {title}
        {sub ? (
          <Typography component="span" variant="caption" color="text.secondary">
            {' '}
            · {sub}
          </Typography>
        ) : null}
      </Typography>
      {action}
    </Box>
    <Box sx={{ px: 2.25, py: 1.75, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {children}
    </Box>
  </Paper>
);

export const InfoRow = ({
  label,
  children,
  hint,
}: {
  label: string;
  children: ReactNode;
  hint?: string;
}): ReactElement => (
  <Box sx={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: 1.5, alignItems: 'baseline' }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, minWidth: 0, flexWrap: 'wrap' }}>
      {children}
      {hint ? (
        <Typography variant="caption" color="text.secondary">
          {hint}
        </Typography>
      ) : null}
    </Box>
  </Box>
);

/** A value or the muted «—». */
export const Value = ({
  children,
  mono = false,
}: {
  children: string | null;
  mono?: boolean;
}): ReactElement => {
  const { t } = useTranslation();
  return (
    <Typography
      variant="body2"
      fontWeight={children ? 500 : 400}
      {...(mono ? { fontFamily: 'monospace' } : {})}
      sx={children ? undefined : { color: tokens.text.tertiary }}
    >
      {children ?? t('companies.noValue')}
    </Typography>
  );
};
